import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { city, country, language } from '../../slices/settingsSlice';
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from 'react-hook-form';
import { setLangCode, setLanguage, updateProfile, onOffNotification } from '../../slices/authSlice';
import { showErrorMessage, showPromiseNotification } from '../../common/notification';
import DatePicker from 'react-datepicker';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { reactSelectStyles } from '../../common/customCss';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { validateNoStartOrEndSpace } from '../../cutomValidationField';
import { useTranslation } from 'react-i18next';
import '../../i18';
import ShareAppModal from '../../modals/shareAppModal/shareAppModal';
import Globals from '../../common/utils';
import { uploadFileToS3 } from '../../common/uploadToS3';

const EditProfile = () => {
    const { t, i18n } = useTranslation();

    const userInfo = useSelector((state) => state.auth.userInfo);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fileRef = useRef(null);
    const fileCertificateRef = useRef(null);

    const [imagePreview, setImagePreview] = useState(null);
    const [govCertificatePreview, setGovCertificatePreview] = useState('');

    const [file, setFile] = useState('');
    const [govCertificate, setGovCertificate] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [progress, setProgress] = useState(0);
    const [isActive, setIsActive] = useState(true);

    const [selectedCountry, setSelectedCountry] = useState(userInfo?.user_country?.id || "");
    const [selectedCity, setSelectedCity] = useState(userInfo?.user_city?.id || "");
    const [selectedLanguage, setSelectedLanguage] = useState(userInfo?.user_language?.id || "");
    const [phone, setPhone] = useState(userInfo?.country_code);
    const [selectedLangCode, setSelectedLangCode] = useState(userInfo?.lang_code || 'en');
    const [selectedLangName, setSelectedLangName] = useState(userInfo?.user_language?.name || 'english');

    const countries = useSelector((state) => state.settings.countryListing);
    const cities = useSelector((state) => state.settings.cityList);
    const languages = useSelector((state) => state.settings.languageList);

    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 18);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const shareUrl = Globals.APP_BASE_URL;
    const title = t("checkoutTheApp");

    useEffect(() => {
        dispatch(country({ limit: 500, sort: "name" }));
        dispatch(language({ limit: 10, sort: "name" }));
    }, [dispatch]);

    useEffect(() => {
        if (selectedCountry) {
            dispatch(city({ limit: 10000, sort: "name", country_id: selectedCountry }));
        } else {
            setSelectedCity('');
        }
    }, [dispatch, selectedCountry]);

    const editSchema = Yup.object().shape({
        full_name: Yup.string().matches(
            /^[a-zA-Z\s'-]*$/,
            t("fullNameValidation", { field: t("Full_Name") })
        ).test('no-start-or-end-space', t('noStartEndSpace', { field: t("Full_Name") }), validateNoStartOrEndSpace).nullable().notRequired(),
        // last_name: Yup.string().matches(
        //     /^[a-zA-Z\s'-]*$/,
        //     t("fullNameValidation", { field: t("Last_Name") })
        // ).test('no-start-or-end-space', t('noStartEndSpace', { field: t("Last_Name") }), validateNoStartOrEndSpace).nullable().notRequired(),
        email: Yup.string()
            .trim()
            .email(t("valid", { field: t("email") }))
            .nullable()
            .notRequired(),
        phone_number: Yup.string()
            .trim()
            .test('is-valid-phone', t("valid", { field: t("phone_number") }), function (value) {
                // If empty or undefined, it's valid (since it's not required)
                if (!value || value === '') {
                    return true;
                }
                // If has value, validate it matches the pattern
                return /^(\+\d{1,3}[- ]?)?\d{10}$/.test(value);
            })
            .nullable()
            .notRequired(),
        country_id: Yup.string().trim().nullable().notRequired(),
        city_id: Yup.string().trim().nullable().notRequired(),
        organization: Yup.string().matches(
            /^[a-zA-Z\s'-]*$/,
            t("fullNameValidation", { field: t("Organization_Name") }))
            .test('no-start-or-end-space', t('noStartEndSpace', { field: t("Organization_Name") }), validateNoStartOrEndSpace).
            nullable().notRequired(),
        experience: Yup.number()
            .nullable()
            .notRequired()
            .typeError(t('experienceValidation'))
            .min(0, t('minExp'))
            .max(50, t('maxExp')),
        language: Yup.string().trim().nullable().notRequired(),
        currency: Yup.string().trim().nullable().notRequired(),
    });

    const formOptions = { resolver: yupResolver(editSchema) };
    const { register, handleSubmit, formState, setError } = useForm(formOptions);
    const { errors } = formState;

    const handleCountryChange = (event) => {
        const value = event.value;
        if (value !== selectedCountry) {
            setSelectedCountry(value);
            setSelectedCity("");
        }
    };

    const handleCityChange = (event) => {
        const value = event.value;
        if (value !== selectedCity) {
            setSelectedCity(value);
        }
    };

    const handleLanguageChange = (event) => {
        const value = event.value;
        const code = event.code;
        const name = event.name;
        if (value !== selectedLanguage) {
            setSelectedLanguage(value);
            setSelectedLangCode(code);
            setSelectedLangName(name);
        }
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleDateInput = (e) => {
        const inputValue = e.target.value;
        const parsedDate = new Date(inputValue);

        // Check if the date is valid
        if (parsedDate instanceof Date && !isNaN(parsedDate)) {
            setSelectedDate(parsedDate);
        }
    };

    const handleKeyDown = (e) => {
        const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "-"];

        if (!/[\d/]/.test(e.key) && !allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const isValidDate = (date) => {
        return date && date !== "0000-00-00" && !isNaN(new Date(date).getTime());
    };

    const uploadImage = () => {
        fileRef.current.click();
    };

    const uploadImageEvent = (e) => {
        const file = e.target.files[0];

        const timestamp = new Date().getTime();
        const newFileName = `${timestamp}_${file.name.replace(/\s+/g, '_')}`;

        const newFile = new File([file], newFileName, { type: file.type });
        setFile(newFile);

        const imagePreviewUrl = URL.createObjectURL(newFile);
        setImagePreview(imagePreviewUrl);
    };

    const uploadCertificateEvent = (e) => {
        const file = e.target.files[0];

        if (file) {
            const timestamp = new Date().getTime();
            const newFileName = `${timestamp}_${file.name.replace(/\s+/g, '_')}`;

            const newFile = new File([file], newFileName, { type: file.type });
            setGovCertificate(newFile);

            // Generate preview URL for the certificate
            const previewUrl = URL.createObjectURL(newFile);
            setGovCertificatePreview(previewUrl);
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard
            .writeText(shareUrl)
            .then(() => {
                setIsModalOpen(true);
            })
            .catch(() => {
                showErrorMessage(t("linkCopyFailed"));
            });
    };

    const handleOrganizationChange = (e) => {
        const value = e.target.value;
        if (/[^a-zA-Z0-9\s'-]/.test(value)) {
            // Display error if emoji or invalid character is typed
            setError('organization', { type: 'manual', message: t("emojiNotAllowed") });
        }
    };

    const onSubmit = useCallback(
        async (values) => {
            values.country_code = phone;
            values.full_name = values.full_name;
            values.dob = selectedDate ?
                `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
                : '';
            values.country_id = selectedCountry;
            values.city_id = selectedCity;
            values.language_id = selectedLanguage;
            delete values.first_name;
            delete values.last_name;

            let imageData = ""
            if (file) {
                imageData = await uploadFileToS3(file, "user_profile_image")
            }

            if (imageData) {
                values.image = imageData.fileName;
            } else {
                values.image = ""
            }

            let govtCertificate = ""
            if (govCertificate) {
                govtCertificate = await uploadFileToS3(govCertificate, "user_government_certificate");
            }


            if (govtCertificate) {
                values.government_certificate = govtCertificate?.fileName;
            } else {
                values.government_certificate = ""
            }
            dispatch(setLangCode({ lang_code: selectedLangCode }))
            const myPromise = new Promise((resolve, reject) => {
                dispatch(
                    updateProfile({
                        payload: {
                            ...values,
                        },
                    })
                )
                    .then((response) => {
                        if (response?.payload?.code == 200) {
                            // localStorage.removeItem("language");
                            setTimeout(() => {
                                i18n.changeLanguage(selectedLangCode);
                                dispatch(setLanguage({ lang_id: selectedLanguage, lang_code: selectedLangCode, name: selectedLangName }));
                                resolve(response.payload);
                                navigate("/profile");
                            }, 1000);
                        }
                        else {
                            reject(response.payload)
                        }
                    })
            });
            showPromiseNotification(myPromise)
        },
        [dispatch, file, govCertificate, selectedDate, selectedCountry, selectedCity, selectedLanguage, phone, selectedLangCode, selectedLangName]
    );

    useEffect(() => {
        const calculateProgress = () => {
            const totalFields = 12; // Total number of form fields
            let filledFields = 0;

            if (userInfo?.full_name) filledFields++;
            if (userInfo?.email) filledFields++;
            if (selectedDate || (isValidDate(userInfo?.dob) ? userInfo?.dob : null)) filledFields++;
            if (selectedCountry) filledFields++;
            if (selectedCity) filledFields++;
            if (selectedLanguage) filledFields++;
            if (file || userInfo?.image) filledFields++;
            if (govCertificate || userInfo?.government_certificate) filledFields++;
            if (phone) filledFields++;
            if (userInfo?.phone_number) filledFields++;
            if (userInfo?.organization) filledFields++;
            if (userInfo?.experience !== null && userInfo?.experience !== "") filledFields++;

            setProgress((filledFields / totalFields) * 100);
        };

        calculateProgress();
    }, [userInfo, userInfo?.experience, selectedDate, selectedCountry, selectedCity, selectedLanguage, file, govCertificate, phone, userInfo?.government_certificate]);

    const handleNotificationChange = useCallback((e) => {
        const myPromise = new Promise((resolve, reject) => {
            dispatch(
                onOffNotification({
                    payload: {
                        is_notify: e.target.checked,
                    },
                })
            )
                .then(async (response) => {
                    if (response.payload.code == 200) {
                        resolve(response.payload);
                    }
                    else {
                        reject(response.payload)
                    }
                });
        });

        showPromiseNotification(myPromise);
    }, []);

    return (
        <>
            <div className="course-single-area pd-top-120 pd-bottom-90 bgBrown">
                <div className="container">
                    <div className="row">
                        <div className="col-xxl-8 col-xl-8 col-lg-8 col-md-12 col-sm-12 col-12">
                            <div className="row">
                                <div className="col-12">
                                    <div className="PageTitleBox">
                                        <h2 className="PageTitleHeading">{t('editProfileHeading')}</h2>
                                    </div>
                                </div>
                                <form action="" onSubmit={handleSubmit(onSubmit)}>
                                    <div className="col-12">
                                        <div className="ProfileMainBox">
                                            <div className="EditProfileImgBox">
                                                <img
                                                    src={imagePreview || userInfo?.image || process.env.PUBLIC_URL + "/assets/img/icon/default.jpg"}
                                                    alt="EditProfileImg"
                                                    id="EditProfileImg"
                                                    className="EditProfileImg img-cover"
                                                    onClick={uploadImage}
                                                />
                                                <div className="EditProfileInputBox">
                                                    <img
                                                        src={process.env.PUBLIC_URL + "/assets/img/icon/EditinputImg.svg"}
                                                        alt="EditinputImg"
                                                        className="EditinputImg img-cover"
                                                    />
                                                    <input
                                                        type="file"
                                                        name='image'
                                                        ref={fileRef}
                                                        className="EditProfileInputFile"
                                                        id="EditProfileInputFile"
                                                        accept="image/*"
                                                        onChange={(e) => uploadImageEvent(e)}
                                                    />
                                                </div>
                                            </div>
                                            <p className="UserNameText">{userInfo?.full_name ? userInfo?.full_name : ''}</p>
                                            <div className="ProgresBarmain">
                                                <div className="progress" role="progressbar" aria-label="Basic example" aria-valuemin={0} aria-valuemax={100}>
                                                    <div
                                                        className="progress-bar"
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                    <span className="ProgressText">{`${Math.round(progress)}%`}</span>
                                                </div>
                                            </div>
                                            <div className="ProFileOtherPageMain">
                                                <div className="row">
                                                    <div className="col-12">
                                                        <div className="ProfileOtherPageBox">
                                                            <div className="single-input-inner w-100">
                                                                <input
                                                                    type="text"
                                                                    className="form-control w-100"
                                                                    placeholder={t("enterFieldName", { field: t('Full_Name') })}
                                                                    name='full_name'
                                                                    defaultValue={userInfo?.full_name ? userInfo?.full_name : ''}
                                                                    {...register('full_name')}
                                                                />
                                                                <p className="text-danger mt-1">
                                                                    {errors.full_name?.message}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-6 col-12">
                                                        <div className="ProfileOtherPageBox">
                                                            <div className="single-input-inner " style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control w-100"
                                                                    placeholder={t("enterFieldName", { field: t('Email') })}
                                                                    name='email'
                                                                    defaultValue={userInfo?.email ? userInfo?.email : ''}
                                                                    {...register('email')}
                                                                />
                                                                <p className="text-danger mt-1">
                                                                    {errors.email?.message}
                                                                </p>
                                                            </div>
                                                            <div className="single-input-inner m-0 w-100">
                                                                <DatePicker
                                                                    selected={selectedDate || (isValidDate(userInfo?.dob) ? userInfo?.dob : null)}
                                                                    onChange={handleDateChange}
                                                                    onInput={handleDateInput}
                                                                    onKeyDown={handleKeyDown}
                                                                    dateFormat="yyyy-MM-dd"
                                                                    placeholderText={t("enterFieldName", { field: t('Date_of_Birth') }) + ": YYYY-MM-DD"}
                                                                    className='form-control w-100'
                                                                    wrapperClassName="w-100"
                                                                    maxDate={maxDate}
                                                                    showYearDropdown
                                                                    scrollableYearDropdown
                                                                    yearDropdownItemNumber={82}
                                                                    dropdownMode="scroll"
                                                                />
                                                                <p className="text-danger mt-1">
                                                                    {errors.dob?.message}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-6 col-12">
                                                        <div className="ProfileOtherPageBox" style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column' }}>
                                                            {/* <div className="single-input-inner style-bg-border" > */}
                                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                                <PhoneInput
                                                                    country={'in'}
                                                                    value={phone}
                                                                    onChange={(phone, data) => {
                                                                        setPhone(data.dialCode)
                                                                    }}
                                                                    inputProps={{
                                                                        name: 'phone',
                                                                        required: true,
                                                                        autoFocus: true,
                                                                        disabled: true
                                                                    }}
                                                                    containerStyle={{ width: '150px', height: '50px' }}
                                                                    inputStyle={{ width: '100%', height: '100%' }}
                                                                />
                                                                <input
                                                                    type="number"
                                                                    className="form-control w-100"
                                                                    placeholder={t("enterFieldName", { field: t('Phone_Number') })}
                                                                    name='phone_number'
                                                                    defaultValue={userInfo?.phone_number ? userInfo?.phone_number : ''}
                                                                    {...register('phone_number')}
                                                                    onChange={(e) => {
                                                                        const inputValue = e.target.value;
                                                                        if (inputValue.length > 10) {
                                                                            e.target.value = inputValue.slice(0, 10);
                                                                        }

                                                                        if (inputValue.length <= 10) {
                                                                            e.target.value = inputValue;
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <p className="text-danger mt-1" style={{ display: 'block', marginTop: '5px' }}>
                                                                {errors.phone_number?.message}
                                                            </p>
                                                            {/* </div> */}
                                                            <div className="SelectBox w-100">
                                                                <div className="ptTwoZero">
                                                                    <div className="SelectBox w-100">
                                                                        <Select name='language' id='language'
                                                                            placeholder={t('selectFieldName', { field: t("Language") })}
                                                                            value={languages?.data?.find(language => language.id === selectedLanguage) ? { value: selectedLanguage, label: languages.data.find(language => language.id === selectedLanguage).native_name } : null}
                                                                            onChange={handleLanguageChange}
                                                                            options={
                                                                                languages?.data?.map((val) => (
                                                                                    { value: val.id, label: val.native_name, code: val.code, name: val.name }
                                                                                ))
                                                                            }
                                                                            styles={reactSelectStyles}
                                                                        />
                                                                        <p className="text-danger mt-1">
                                                                            {errors.language?.message}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-6 col-12">
                                                        <div className="ProfileOtherPageBox" style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column' }}>
                                                            <div className="SelectBox w-100" >
                                                                <Select name='country_id' id='country'
                                                                    placeholder={t('selectFieldName', { field: t("Country") })}
                                                                    value={countries?.data?.find(country => country.id === selectedCountry) ? { value: selectedCountry, label: countries.data.find(country => country.id === selectedCountry).name } : null}
                                                                    onChange={handleCountryChange}
                                                                    options={
                                                                        countries?.data?.map((val) => (
                                                                            { value: val.id, label: val.name }
                                                                        ))
                                                                    }
                                                                    styles={reactSelectStyles}
                                                                />
                                                                <p className="text-danger mt-1">
                                                                    {errors.country_id?.message}
                                                                </p>
                                                            </div>
                                                            <div className="SelectBox mt-3">
                                                                <Select name='city_id' id='city'
                                                                    placeholder={t('selectFieldName', { field: t("City") })}
                                                                    value={cities?.data?.find(city => city.id === selectedCity) ? { value: selectedCity, label: cities.data.find(city => city.id === selectedCity).name } : null}
                                                                    onChange={handleCityChange}
                                                                    disabled={!setSelectedCountry}
                                                                    options={selectedCountry ?
                                                                        cities?.data?.map((val) => (
                                                                            { value: val.id, label: val.name }
                                                                        )) : []
                                                                    }
                                                                    styles={reactSelectStyles}
                                                                />
                                                                <p className="text-danger mt-1">
                                                                    {errors.city_id?.message}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-6 col-12">
                                                        <div className="ProfileOtherPageBox">
                                                            <div className="cloud">
                                                                {/* Conditional rendering for certificate preview */}
                                                                {govCertificatePreview || userInfo?.government_certificate ? (
                                                                    <img
                                                                        src={
                                                                            govCertificatePreview || // Use uploaded certificate preview
                                                                            userInfo?.government_certificate // Use existing certificate if available
                                                                        }
                                                                        alt="GovCertificatePreview"
                                                                        className="certificate-preview img-cover"
                                                                        style={{ maxWidth: '100px', marginBottom: '10px', maxHeight: '70px', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <img
                                                                        src={process.env.PUBLIC_URL + "/assets/img/icon/cloud1.svg"}
                                                                        alt="cloud"
                                                                        className="cloud-img"
                                                                    />
                                                                )}
                                                                <span className="upload-text">
                                                                    {
                                                                        (govCertificate || userInfo?.government_certificate) ?
                                                                            "" :
                                                                            t("govCerti")
                                                                    }
                                                                </span>
                                                                <input
                                                                    type="file"
                                                                    className="form-control apply-input-file upload-text"
                                                                    placeholder={t('selectFieldName', { field: t("Subject") })}
                                                                    id="field2"
                                                                    name="government_certificate"
                                                                    accept="image/*"
                                                                    ref={fileCertificateRef}
                                                                    onChange={(e) => uploadCertificateEvent(e)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-6 col-12">
                                                        <div className="ProfileOtherPageBox">
                                                            <div className="single-input-inner m-0 w-100">
                                                                <input
                                                                    type="text"
                                                                    className="form-control w-100"
                                                                    placeholder={t("enterFieldName", { field: t('Organization_Name') })}
                                                                    name='organization'
                                                                    defaultValue={userInfo?.organization ? userInfo?.organization : ''}
                                                                    {...register('organization')}
                                                                    onChange={(e) => {
                                                                        handleOrganizationChange(e);
                                                                    }}
                                                                />
                                                                <p className="text-danger mt-1">
                                                                    {errors.organization?.message}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-6 col-12">
                                                        <div className="ProfileOtherPageBox">
                                                            <div className="single-input-inner m-0 w-100">
                                                                <input
                                                                    type="text"
                                                                    className="form-control w-100"
                                                                    placeholder={t("enterFieldName", { field: t('Experience_in_Years') })}
                                                                    name='experience'
                                                                    defaultValue={userInfo?.experience || 0}
                                                                    {...register('experience')}
                                                                />
                                                                <p className="text-danger mt-1">
                                                                    {errors.experience?.message}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-6 col-12">
                                                        <div className="ptTwoZero">
                                                            <div className="SelectBox w-100">
                                                                <Select name="currency" id="currency"
                                                                    placeholder={t('selectFieldName', { field: t("Currency") })}
                                                                    options={[
                                                                        { value: "AFN", label: "Afghanistan" },
                                                                        { value: "ALL", label: "Albania" },
                                                                        { value: "DZD", label: "Algeria" },
                                                                    ]}
                                                                    styles={reactSelectStyles}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div> */}
                                                    <div className="col-12">
                                                        <div className="ProfileOtherPageBox text-center">
                                                            <button className='btn btn-base w-50' type='submit'>{t('submit')}</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-12 col-sm-12 col-12">
                            <ul className="PageTitleBoxUl">
                                <li className="PageTitleBoxLi">
                                    <div className="PageTitleBox">
                                        <h2 className="PageTitleHeading">{t('otherProfileLinksHeading')}</h2>
                                    </div>
                                </li>
                                <li className="PageTitleBoxLi">
                                    <div className="ProfileOtherPageBox">
                                        <div className="ProfileOtherPageBoxcard">
                                            <p htmlFor="notification" className="ProfileOtherPageText">
                                                <img
                                                    src={process.env.PUBLIC_URL + "/assets/img/icon/notificationbell.svg"}
                                                    alt="notificationbell"
                                                    className="IconTwoFive"
                                                />
                                                {t('notiSettings')}
                                            </p>
                                            <div className="toggle-main" id="notification">
                                                <label className={`switch ${userInfo?.is_notify ? 'active' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        id="name"
                                                        checked={userInfo?.is_notify}
                                                        onChange={(e) => handleNotificationChange(e)}
                                                    />
                                                    <span className="slider round" />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                                <li className="PageTitleBoxLi">
                                    <div className="ProfileOtherPageBox">
                                        <div className="ProfileOtherPageBoxcard">
                                            <Link to="/animation-videos" className="ProfileOtherPageLink">
                                                <img
                                                    src={process.env.PUBLIC_URL + "/assets/img/icon/animetionvideo.svg"}
                                                    alt="animetionvideo"
                                                    className="IconTwoFive"
                                                />
                                                {t('animationVideosHeading')}
                                            </Link>
                                        </div>
                                    </div>
                                </li>
                                <li className="PageTitleBoxLi">
                                    <div className="ProfileOtherPageBox">
                                        <div className="ProfileOtherPageBoxcard">
                                            <a href='#' className="ProfileOtherPageLink"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleCopyLink();
                                                }}
                                            >
                                                <img
                                                    src={process.env.PUBLIC_URL + "/assets/img/icon/ShareAppIcon.svg"}
                                                    alt="ShareAppIcon"
                                                    className="IconTwoFive"
                                                />
                                                {t('shareApp')}
                                            </a>
                                        </div>
                                        {isModalOpen && (
                                            <ShareAppModal
                                                isOpen={isModalOpen}
                                                isClose={() => setIsModalOpen(false)}
                                                shareUrl={shareUrl}
                                                title={title}
                                            />
                                        )}
                                    </div>
                                </li>
                                <li className="PageTitleBoxLi">
                                    <div className="ProfileOtherPageBox">
                                        <div className="ProfileOtherPageBoxcard">
                                            <Link
                                                to="/complaint-feedback"
                                                className="ProfileOtherPageLink"
                                            >
                                                <img
                                                    src={process.env.PUBLIC_URL + "/assets/img/icon/forms.png"}
                                                    alt="forms"
                                                    className="IconTwoFive"
                                                />
                                                {t('complaintFeedbackFormHeading')}
                                            </Link>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>

    )
}

export default EditProfile;